import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

interface NavbarProps {
  scrolled?: boolean;
}

export default function Navbar({ scrolled = false }: NavbarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const services = [
    { name: 'Office Cleaning', path: '/services/office-cleaning' },
    { name: 'Retail Cleaning', path: '/services/retail-cleaning' },
    { name: 'Medical Facility Cleaning', path: '/services/medical-facility-cleaning' },
    { name: 'Industrial Cleaning', path: '/services/industrial-cleaning' },
    { name: 'Window Cleaning', path: '/services/window-cleaning' },
    { name: 'Specialized Services', path: '/services/specialized-services' },
  ];

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileServicesOpen(false);
    setServicesDropdownOpen(false);
  }, [location.pathname]);

  const scrollToSection = (id: string) => {
    setMobileMenuOpen(false);
    setServicesDropdownOpen(false);
    
    // If not on home page, navigate to home first
    if (location.pathname !== '/') {
      navigate('/', { state: { scrollTo: id } });
      return;
    }
    
    const element = document.getElementById(id);
    if (!element) {
      console.warn(`scrollToSection: No element found with id "${id}"`);
      return;
    }
    const offset = 80;
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - offset;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth',
    });
  };

  // Close services dropdown when clicking outside of it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setServicesDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white shadow-lg' : 'bg-white shadow-md'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div
            className="flex items-center gap-3 cursor-pointer"
            onClick={() => {
              navigate('/');
              window.scrollTo({ top: 0, behavior: 'smooth' });
              setMobileMenuOpen(false);
            }}
          >
            <div className="w-10 h-10 flex items-center justify-center bg-teal-500 rounded-lg">
              <i className="ri-sparkling-2-fill text-white text-xl"></i>
            </div>
            <span className="text-xl font-bold text-slate-800 font-['Poppins']">
              First Insight Cleaning
            </span>
          </div>

          {/* Desktop Navigation Links */}
          <div className="hidden md:flex items-center gap-8">
            {/* Services Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setServicesDropdownOpen(!servicesDropdownOpen)}
                className="flex items-center gap-1 text-slate-600 hover:text-teal-500 transition-colors font-medium whitespace-nowrap cursor-pointer"
              >
                Services
                <i
                  className={`ri-arrow-down-s-line transition-transform duration-300 ${
                    servicesDropdownOpen ? 'rotate-180' : ''
                  }`}
                ></i>
              </button>

              {/* Dropdown Menu */}
              {servicesDropdownOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-72 bg-white rounded-xl shadow-xl border border-slate-100 py-3 animate-fadeIn">
                  {services.map((service, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        navigate(service.path);
                        setServicesDropdownOpen(false);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      className="block w-full text-left px-6 py-3.5 text-slate-700 hover:bg-teal-50 hover:text-teal-500 transition-colors cursor-pointer font-medium"
                    >
                      {service.name}
                    </button>
                  ))}
                  <div className="border-t border-slate-100 my-2"></div>
                  <button
                    onClick={() => {
                      scrollToSection('services');
                      setServicesDropdownOpen(false);
                    }}
                    className="w-full text-left px-6 py-3.5 text-teal-500 font-semibold hover:bg-teal-50 transition-colors cursor-pointer"
                  >
                    View All Services
                  </button>
                </div>
              )}
            </div>

            <button
              onClick={() => scrollToSection('testimonials')}
              className="text-slate-600 hover:text-teal-500 transition-colors font-medium whitespace-nowrap cursor-pointer"
            >
              Testimonials
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className="text-slate-600 hover:text-teal-500 transition-colors font-medium whitespace-nowrap cursor-pointer"
            >
              Contact
            </button>
          </div>

          {/* Contact & CTA */}
          <div className="flex items-center gap-4">
            <a
              href="tel:+15028308648"
              className="hidden lg:flex items-center gap-2 text-teal-500 font-semibold whitespace-nowrap"
            >
              <i className="ri-phone-fill"></i>
              <span>(502) 830-8648</span>
            </a>
            <button
              onClick={() => scrollToSection('quote-form')}
              className="hidden sm:block bg-teal-500 hover:bg-teal-600 text-white px-6 py-2.5 rounded-full font-semibold transition-all duration-300 hover:shadow-lg whitespace-nowrap cursor-pointer"
            >
              Get Free Quote
            </button>

            {/* Mobile Hamburger */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden flex items-center justify-center w-10 h-10 text-slate-700 cursor-pointer"
            >
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 top-20 bg-white z-40 overflow-y-auto">
          <div className="px-6 py-6 flex flex-col gap-2">
            {/* Services Dropdown */}
            <div>
              <button
                onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                className="flex items-center justify-between w-full py-3 text-slate-700 font-semibold text-lg cursor-pointer"
              >
                <span>Services</span>
                <i
                  className={`ri-arrow-down-s-line text-xl transition-transform duration-300 ${
                    mobileServicesOpen ? 'rotate-180' : ''
                  }`}
                ></i>
              </button>
              {mobileServicesOpen && (
                <div className="pl-4 flex flex-col gap-1 mb-2">
                  {services.map((service, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        navigate(service.path);
                        setMobileMenuOpen(false);
                        setMobileServicesOpen(false);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      className="py-2.5 px-3 text-slate-600 hover:text-teal-500 hover:bg-teal-50 rounded-lg transition-colors cursor-pointer text-left"
                    >
                      {service.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={() => scrollToSection('testimonials')}
              className="py-3 text-left text-slate-700 font-semibold text-lg cursor-pointer hover:text-teal-500 transition-colors"
            >
              Testimonials
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className="py-3 text-left text-slate-700 font-semibold text-lg cursor-pointer hover:text-teal-500 transition-colors"
            >
              Contact
            </button>

            <div className="border-t border-slate-100 my-3"></div>

            <a
              href="tel:+15028308648"
              className="flex items-center gap-2 py-3 text-teal-500 font-semibold text-lg"
            >
              <i className="ri-phone-fill"></i>
              <span>(502) 830-8648</span>
            </a>

            <button
              onClick={() => scrollToSection('quote-form')}
              className="mt-2 bg-teal-500 hover:bg-teal-600 text-white px-6 py-3 rounded-full font-semibold transition-all duration-300 text-center whitespace-nowrap cursor-pointer"
            >
              Get Free Quote
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}
